import ProtectedRoute from "@/components/ProtectedRoute";
import RoleGate from "@/components/RoleGate";
import Table from "@/components/Base/Table";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getRole, getToken } from "@/utils/auth";
import Lucide from "@/components/Base/Lucide";
import Pagination from "@/components/Base/Pagination";
import { FormInput, FormSelect } from "@/components/Base/Form";
import Button from "@/components/Base/Button";

type Product = {
    id: string;
    name: string;
    sku?: string | null;
    productLine?: string | null;
    status?: boolean | null;
    displayOrder?: number | null;
    createdAt?: string;
};

type OutletCategory = {
    id: string;
    catName: string;
};

const formatUpTo6DigitsWithThousands = (value: string) => {
    const digits = (value || "").replace(/[^\d]/g, "").slice(0, 6);
    if (!digits) return "";
    const n = Number(digits);
    if (!Number.isFinite(n)) return "";
    return n.toLocaleString("en-US");
};

const isAllowedNumericKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Allow navigation/editing keys
    const allowed = new Set([
        "Backspace",
        "Delete",
        "Tab",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
        "Enter",
    ]);
    if (allowed.has(e.key)) return true;

    // Allow copy/paste/select all
    if (e.metaKey || e.ctrlKey) return true;

    // Allow digits and comma (comma will be normalized anyway)
    if (/^\d$/.test(e.key)) return true;
    if (e.key === ",") return true;

    return false;
};

const isOwnProductLine = (productLine?: string | null) => {
    const v = (productLine || "").toLowerCase().trim();
    return v === "" || v.startsWith("own");
};

const sortProducts = (a: Product, b: Product) => {
    const ao = a.displayOrder;
    const bo = b.displayOrder;
    if (ao !== null && ao !== undefined && bo !== null && bo !== undefined) return ao - bo;
    if (ao !== null && ao !== undefined && (bo === null || bo === undefined)) return -1;
    if ((ao === null || ao === undefined) && bo !== null && bo !== undefined) return 1;
    const at = a.createdAt ? new Date(a.createdAt).getTime() : 0;
    const bt = b.createdAt ? new Date(b.createdAt).getTime() : 0;
    return bt - at;
};

export default function OOSThreshold() {
    const { projectId } = useParams<{ projectId: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [products, setProducts] = useState<Product[]>([]);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchInput, setSearchInput] = useState("");
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [effectiveProductLine, setEffectiveProductLine] = useState<"Own product" | "Own">(
        "Own product"
    );
    const [outletCategories, setOutletCategories] = useState<OutletCategory[]>([]);
    const [loadingOutletCategories, setLoadingOutletCategories] = useState(false);
    const [outletCategoriesError, setOutletCategoriesError] = useState<string | null>(null);
    const [thresholdByProductCategoryKey, setThresholdByProductCategoryKey] = useState<Record<string, string>>({});
    const [isEditing, setIsEditing] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccessToast, setShowSuccessToast] = useState(false);

    const role = useMemo(() => getRole(), []);
    const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

    useEffect(() => {
        if (!showSuccessToast) return;
        const t = window.setTimeout(() => setShowSuccessToast(false), 3000);
        return () => window.clearTimeout(t);
    }, [showSuccessToast]);

    useEffect(() => {
        let cancelled = false;

        const fetchProjectCompanyId = async () => {
            if (!projectId) return null;
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const res = await fetch(`${apiUrl}/projects/${projectId}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) return null;
            const json = await res.json();
            return json?.data?.companyId || null;
        };

        const probeHasAnyProductLine = async (productLine: string, resolvedCompanyId: string | null) => {
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const params = new URLSearchParams();
            params.set("page", "1");
            params.set("pageSize", "1");
            params.set("productLine", productLine);
            if (resolvedCompanyId) params.set("companyId", resolvedCompanyId);

            const res = await fetch(`${apiUrl}/products?${params.toString()}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) return false;
            const json = await res.json();
            return Number(json?.total || 0) > 0;
        };

        (async () => {
            try {
                setError(null);

                // SUPERADMIN needs companyId for /products scoping.
                const resolvedCompanyId =
                    role === "SUPERADMIN" ? await fetchProjectCompanyId() : null;

                if (!cancelled) setCompanyId(resolvedCompanyId);

                if (role === "SUPERADMIN" && !resolvedCompanyId) {
                    if (!cancelled) {
                        setError("Unable to determine company for this project.");
                    }
                    return;
                }

                // Pick a single productLine value that actually exists in DB for this company.
                const hasOwnProduct = await probeHasAnyProductLine("Own product", resolvedCompanyId);
                if (!cancelled) setEffectiveProductLine(hasOwnProduct ? "Own product" : "Own");
            } catch (e: any) {
                if (!cancelled) setError(e?.message || "Failed to load products");
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [projectId, role]);

    useEffect(() => {
        let cancelled = false;

        const fetchAllOutletCategories = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const take = 200;
            let page = 1;
            let totalPagesLocal = 1;
            const all: OutletCategory[] = [];

            while (page <= totalPagesLocal) {
                const params = new URLSearchParams();
                params.set("page", String(page));
                params.set("pageSize", String(take));
                params.set("sortBy", "catName");
                params.set("sortOrder", "asc");
                if (role === "SUPERADMIN" && companyId) params.set("companyId", companyId);

                const res = await fetch(`${apiUrl}/outlet-categories?${params.toString()}`, {
                    headers: { Authorization: `Bearer ${getToken()}` },
                });
                if (!res.ok) throw new Error("Failed to fetch outlet categories");
                const json = await res.json();
                const data = Array.isArray(json?.data) ? (json.data as OutletCategory[]) : [];
                all.push(...data);
                totalPagesLocal = Number(json?.totalPages || 1);
                page += 1;
            }

            return all;
        };

        (async () => {
            try {
                setLoadingOutletCategories(true);
                setOutletCategoriesError(null);

                if (role === "SUPERADMIN" && !companyId) {
                    // Wait for companyId to be resolved from project
                    setOutletCategories([]);
                    return;
                }

                const cats = await fetchAllOutletCategories();
                if (!cancelled) setOutletCategories(cats);
            } catch (e: any) {
                if (!cancelled) setOutletCategoriesError(e?.message || "Failed to load outlet categories");
            } finally {
                if (!cancelled) setLoadingOutletCategories(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [companyId, role]);

    useEffect(() => {
        let cancelled = false;

        const fetchProductsPage = async () => {
            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const params = new URLSearchParams();
            params.set("page", String(currentPage));
            params.set("pageSize", String(pageSize));
            params.set("productLine", effectiveProductLine);
            if (searchQuery) params.set("q", searchQuery);
            if (role === "SUPERADMIN" && companyId) params.set("companyId", companyId);

            const res = await fetch(`${apiUrl}/products?${params.toString()}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("Failed to fetch products");
            const json = await res.json();

            const data = Array.isArray(json?.data) ? (json.data as Product[]) : [];
            const normalized = data.filter((p) => isOwnProductLine(p.productLine)).sort(sortProducts);

            if (cancelled) return;
            setProducts(normalized);
            setTotalPages(Number(json?.totalPages || 1));
            setTotal(Number(json?.total || 0));
        };

        (async () => {
            try {
                setLoading(true);
                setError(null);

                if (role === "SUPERADMIN" && !companyId) {
                    // Wait for companyId to be resolved from project
                    setProducts([]);
                    setTotal(0);
                    setTotalPages(1);
                    return;
                }

                await fetchProductsPage();
            } catch (e: any) {
                if (!cancelled) setError(e?.message || "Failed to load products");
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [companyId, currentPage, effectiveProductLine, pageSize, role, searchQuery]);

    const handleSearch = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setSearchQuery(searchInput);
        setCurrentPage(1);
    };

    const colCount = 2 + outletCategories.length;
    const cellKey = (productId: string, categoryId: string) => `${productId}__${categoryId}`;
    const inputId = (rowIndex: number, colIndex: number) => `oos-threshold-${rowIndex}-${colIndex}`;
    const focusVertical = (rowIndex: number, colIndex: number, delta: 1 | -1) => {
        let r = rowIndex + delta;
        while (r >= 0 && r < products.length) {
            const el = inputRefs.current[inputId(r, colIndex)];
            if (el && !el.disabled) {
                el.focus();
                // keep it convenient for quick edits
                el.select?.();
                return true;
            }
            r += delta;
        }
        return false;
    };

    useEffect(() => {
        let cancelled = false;

        const fetchThresholdsForCurrentPage = async () => {
            if (!projectId) return;
            if (!products.length || !outletCategories.length) return;

            const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";
            const params = new URLSearchParams();
            params.set("projectId", projectId);
            params.set("productIds", products.map((p) => p.id).join(","));
            params.set("outletCategories", outletCategories.map((c) => c.id).join(","));

            const res = await fetch(`${apiUrl}/stock-thresholds?${params.toString()}`, {
                headers: { Authorization: `Bearer ${getToken()}` },
            });
            if (!res.ok) throw new Error("Failed to load thresholds");
            const json = await res.json();
            const data = Array.isArray(json?.data) ? json.data : [];

            const next: Record<string, string> = {};
            for (const r of data) {
                const k = cellKey(String(r.productId), String(r.outletCategory));
                next[k] = formatUpTo6DigitsWithThousands(String(r.threshold ?? ""));
            }
            return next;
        };

        (async () => {
            try {
                // Don't overwrite user input while editing
                if (isEditing) return;
                const next = await fetchThresholdsForCurrentPage();
                if (!next || cancelled) return;
                setThresholdByProductCategoryKey((prev) => ({ ...prev, ...next }));
            } catch (e) {
                // keep silent; UI still works without prefill
                console.error(e);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [currentPage, isEditing, outletCategories, products, projectId]);

    return (
        <ProtectedRoute>
            <RoleGate allow={["SUPERADMIN", "ADMIN"]}>
                {showSuccessToast && (
                    <div className="fixed top-[85px] right-5 z-[80]">
                        <div className="py-4 pl-4 pr-10 bg-white border border-slate-200/60 rounded-lg shadow-xl dark:bg-darkmode-600 dark:text-slate-300 dark:border-darkmode-600 flex items-center gap-3">
                            <Lucide icon="CheckCircle" className="w-5 h-5 text-success stroke-[1.3]" />
                            <div>
                                <div className="font-medium">Success</div>
                                <div className="text-slate-500 dark:text-slate-400 text-sm">
                                    Thresholds stored successfully.
                                </div>
                            </div>
                            <button
                                type="button"
                                className="absolute top-2 right-2 text-slate-500 hover:text-slate-700 dark:text-slate-300 dark:hover:text-slate-100"
                                onClick={() => setShowSuccessToast(false)}
                                aria-label="Close"
                            >
                                <Lucide icon="X" className="w-4 h-4 stroke-[1.3]" />
                            </button>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-12 gap-y-10 gap-x-6">
                    <div className="col-span-12">
                        <div className="flex items-center h-10">
                            <div className="text-base font-medium group-[.mode--light]:text-white">
                                Set Out Of Stock (OOS) Threshold
                            </div>
                            <div className="ml-3 text-slate-500 dark:text-slate-400 text-sm">
                                {loading ? "Loading..." : `${total} Own products`}
                            </div>
                        </div>

                        <div className="flex flex-col gap-8 mt-3.5">
                            <div className="flex flex-col box box--stacked">
                                <div className="flex flex-col p-5 sm:items-center sm:flex-row gap-y-2">
                                    <div>
                                        <form onSubmit={handleSearch}>
                                            <div className="relative">
                                                <Lucide
                                                    icon="Search"
                                                    className="absolute inset-y-0 left-0 z-10 w-4 h-4 my-auto ml-3 stroke-[1.3] text-slate-500"
                                                />
                                                <FormInput
                                                    type="text"
                                                    placeholder="Search products..."
                                                    className="pl-9 sm:w-64 rounded-[0.5rem]"
                                                    value={searchInput}
                                                    onChange={(e) => setSearchInput(e.target.value)}
                                                />
                                            </div>
                                        </form>
                                    </div>
                                    <div className="flex flex-col sm:flex-row gap-x-2 gap-y-2 sm:ml-auto">
                                        <Button
                                            variant="success"
                                            className="w-full sm:w-auto"
                                            disabled={isSubmitting || !projectId || loadingOutletCategories}
                                            onClick={async () => {
                                                try {
                                                    if (!projectId) return;
                                                    setIsSubmitting(true);

                                                    const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:4000";

                                                    const items = products.flatMap((p) =>
                                                        outletCategories.map((c) => {
                                                            const v =
                                                                thresholdByProductCategoryKey[cellKey(p.id, c.id)] ?? "0";
                                                            const digits = String(v).replace(/[^\d]/g, "");
                                                            const threshold = digits ? Number(digits) : 0;
                                                            return {
                                                                productId: p.id,
                                                                sku: p.sku || "",
                                                                outletCategory: c.id,
                                                                threshold,
                                                            };
                                                        })
                                                    );

                                                    const res = await fetch(`${apiUrl}/stock-thresholds/bulk`, {
                                                        method: "POST",
                                                        headers: {
                                                            Authorization: `Bearer ${getToken()}`,
                                                            "Content-Type": "application/json",
                                                        },
                                                        body: JSON.stringify({ projectId, items }),
                                                    });

                                                    if (!res.ok) {
                                                        const err = await res.json().catch(() => ({}));
                                                        throw new Error(err?.error || err?.message || "Submit failed");
                                                    }

                                                    setShowSuccessToast(true);
                                                    setIsEditing(false);
                                                } catch (e: any) {
                                                    alert(e?.message || "Failed to submit thresholds");
                                                } finally {
                                                    setIsSubmitting(false);
                                                }
                                            }}
                                        >
                                            Submit
                                        </Button>
                                        <Button
                                            variant="warning"
                                            className="w-full sm:w-auto"
                                            disabled={isSubmitting}
                                            onClick={() => setIsEditing(true)}
                                        >
                                            Edit
                                        </Button>
                                        <Button
                                            variant="outline-danger"
                                            className="w-full sm:w-auto"
                                            disabled={isSubmitting}
                                            onClick={() => {
                                                const next: Record<string, string> = {};
                                                for (const p of products) {
                                                    for (const c of outletCategories) {
                                                        next[cellKey(p.id, c.id)] = "0";
                                                    }
                                                }
                                                setThresholdByProductCategoryKey((prev) => ({ ...prev, ...next }));
                                            }}
                                        >
                                            Reset All
                                        </Button>
                                    </div>
                                </div>

                                <div className="max-w-full overflow-x-auto">
                                    <Table className="border-b border-slate-200/60 text-sm table-fixed w-full">
                                        <Table.Thead className="bg-white dark:bg-darkmode-600">
                                            <Table.Tr>
                                                <Table.Td className="py-2 font-medium w-[420px] pl-3 pr-1">
                                                    Product
                                                </Table.Td>
                                                <Table.Td className="py-2 font-medium w-[180px] pl-1 pr-5">
                                                    SKU
                                                </Table.Td>
                                                {outletCategories.map((c, idx) => (
                                                    <Table.Td
                                                        key={c.id}
                                                        className={`
                                                            py-2 font-medium text-xs min-w-[120px] px-2 whitespace-normal break-words leading-snug text-center
                                                            ${idx === 0 ? "border-l-2 border-slate-200/60 dark:border-darkmode-300 pl-5" : ""}
                                                        `}
                                                    >
                                                        {c.catName}
                                                    </Table.Td>
                                                ))}
                                            </Table.Tr>
                                        </Table.Thead>
                                        <Table.Tbody>
                                            {loading ? (
                                                <Table.Tr>
                                                    <Table.Td className="py-4 text-slate-500" colSpan={colCount}>
                                                        Loading products...
                                                    </Table.Td>
                                                </Table.Tr>
                                            ) : error ? (
                                                <Table.Tr>
                                                    <Table.Td className="py-4 text-danger" colSpan={colCount}>
                                                        {error}
                                                    </Table.Td>
                                                </Table.Tr>
                                            ) : products.length === 0 ? (
                                                <Table.Tr>
                                                    <Table.Td className="py-4 text-slate-500" colSpan={colCount}>
                                                        No products found.
                                                    </Table.Td>
                                                </Table.Tr>
                                            ) : (
                                                products.map((p, rowIndex) => (
                                                    <Table.Tr key={p.id} className="[&_td]:last:border-b-0">
                                                        <Table.Td className="py-2 w-[420px] max-w-[420px] truncate pl-3 pr-1">
                                                            {p.name || "-"}
                                                        </Table.Td>
                                                        <Table.Td className="py-2 w-[180px] whitespace-nowrap text-slate-600 dark:text-slate-300 pl-1 pr-5">
                                                            {p.sku || "-"}
                                                        </Table.Td>
                                                        {outletCategories.map((c, idx) => (
                                                            <Table.Td
                                                                key={`${p.id}_${c.id}`}
                                                                className={`
                                                                    py-2 text-xs min-w-[120px] px-2 text-center
                                                                    ${idx === 0 ? "border-l-2 border-slate-200/60 dark:border-darkmode-300 pl-5" : ""}
                                                                `}
                                                            >
                                                                <FormInput
                                                                    id={inputId(rowIndex, idx)}
                                                                    ref={(el) => {
                                                                        const id = inputId(rowIndex, idx);
                                                                        if (el) inputRefs.current[id] = el;
                                                                        else delete inputRefs.current[id];
                                                                    }}
                                                                    inputMode="numeric"
                                                                    placeholder="0"
                                                                    className="w-[9ch] h-7 rounded-[0.5rem] text-center text-xs tabular-nums px-1.5"
                                                                    disabled={!isEditing || isSubmitting}
                                                                    value={
                                                                        thresholdByProductCategoryKey[
                                                                            cellKey(p.id, c.id)
                                                                        ] ?? ""
                                                                    }
                                                                    onKeyDown={(e) => {
                                                                        if (!isEditing || isSubmitting) return;
                                                                        if (e.key === "Tab" || e.key === "Enter") {
                                                                            const moved = focusVertical(
                                                                                rowIndex,
                                                                                idx,
                                                                                e.shiftKey ? -1 : 1
                                                                            );
                                                                            if (moved) e.preventDefault();
                                                                            return;
                                                                        }
                                                                        if (!isAllowedNumericKey(e)) e.preventDefault();
                                                                    }}
                                                                    onPaste={(e) => {
                                                                        if (!isEditing || isSubmitting) return;
                                                                        const text = e.clipboardData.getData("text");
                                                                        const formatted =
                                                                            formatUpTo6DigitsWithThousands(text);
                                                                        const key = cellKey(p.id, c.id);
                                                                        setThresholdByProductCategoryKey((prev) => ({
                                                                            ...prev,
                                                                            [key]: formatted,
                                                                        }));
                                                                        e.preventDefault();
                                                                    }}
                                                                    onChange={(e) => {
                                                                        if (!isEditing || isSubmitting) return;
                                                                        const formatted =
                                                                            formatUpTo6DigitsWithThousands(
                                                                                e.target.value
                                                                            );
                                                                        const key = cellKey(p.id, c.id);
                                                                        setThresholdByProductCategoryKey((prev) => ({
                                                                            ...prev,
                                                                            [key]: formatted,
                                                                        }));
                                                                    }}
                                                                />
                                                            </Table.Td>
                                                        ))}
                                                    </Table.Tr>
                                                ))
                                            )}
                                            {!loading && !error && outletCategoriesError && (
                                                <Table.Tr>
                                                    <Table.Td className="py-3 text-warning" colSpan={colCount}>
                                                        Outlet categories failed to load: {outletCategoriesError}
                                                    </Table.Td>
                                                </Table.Tr>
                                            )}
                                        </Table.Tbody>
                                    </Table>
                                </div>

                                <div className="flex flex-col-reverse flex-wrap items-center p-5 flex-reverse gap-y-2 sm:flex-row">
                                    <div className="text-sm text-slate-500 mr-4">
                                        {total > 0 ? (
                                            <>
                                                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                                                {Math.min(currentPage * pageSize, total)} of {total} products
                                            </>
                                        ) : (
                                            "No products found"
                                        )}
                                    </div>

                                    <Pagination className="flex-1 w-full mr-auto sm:w-auto">
                                        {currentPage > 1 && (
                                            <Pagination.Link onClick={() => setCurrentPage(1)}>
                                                <Lucide icon="ChevronsLeft" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {currentPage > 1 && (
                                            <Pagination.Link onClick={() => setCurrentPage(currentPage - 1)}>
                                                <Lucide icon="ChevronLeft" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {totalPages > 7 && currentPage > 4 && (
                                            <>
                                                <Pagination.Link onClick={() => setCurrentPage(1)}>1</Pagination.Link>
                                                {currentPage > 5 && <Pagination.Link>...</Pagination.Link>}
                                            </>
                                        )}
                                        {Array.from({ length: totalPages }).map((_, i) => {
                                            const pageNum = i + 1;
                                            if (
                                                totalPages <= 7 ||
                                                pageNum <= 3 ||
                                                pageNum > totalPages - 3 ||
                                                (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                                            ) {
                                                return (
                                                    <Pagination.Link
                                                        key={i}
                                                        active={currentPage === pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Pagination.Link>
                                                );
                                            }
                                            return null;
                                        })}
                                        {totalPages > 7 && currentPage < totalPages - 3 && (
                                            <>
                                                {currentPage < totalPages - 4 && <Pagination.Link>...</Pagination.Link>}
                                                <Pagination.Link onClick={() => setCurrentPage(totalPages)}>
                                                    {totalPages}
                                                </Pagination.Link>
                                            </>
                                        )}
                                        {currentPage < totalPages && (
                                            <Pagination.Link onClick={() => setCurrentPage(currentPage + 1)}>
                                                <Lucide icon="ChevronRight" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                        {currentPage < totalPages && (
                                            <Pagination.Link onClick={() => setCurrentPage(totalPages)}>
                                                <Lucide icon="ChevronsRight" className="w-4 h-4" />
                                            </Pagination.Link>
                                        )}
                                    </Pagination>

                                    <FormSelect
                                        className="sm:w-20 rounded-[0.5rem]"
                                        value={pageSize}
                                        onChange={(e) => {
                                            setPageSize(Number(e.target.value));
                                            setCurrentPage(1);
                                        }}
                                    >
                                        <option value={10}>10</option>
                                        <option value={20}>20</option>
                                        <option value={25}>25</option>
                                        <option value={50}>50</option>
                                        <option value={100}>100</option>
                                    </FormSelect>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </RoleGate>
        </ProtectedRoute>
    );
}


